// frontend/src/constants/planning.js
//
// Planning constraints for the store-level settings (Store Settings) and the
// per-supplier review cadence (Supplier setup grid, create-supplier dialog).
//
// PLANNING_HORIZON_DAYS mirrors `_VALID_PLANNING_HORIZONS` in
// backend/authentication/views/stores.py. That endpoint 400s on any other
// value.
//
// The review-frequency constants below mirror backend/core/models.py the same
// way, and unlike the horizons they are ASSERTED against it, by
// backend/purchase_orders/test_enum_mirror_contracts.py (which reads this file).
//
// Labels stay at the call sites, through `t()`.

export const PLANNING_HORIZON_DAYS = [30, 60, 90, 120, 150, 180];

// Wire keys of the `planning` block on /auth/stores/settings/ — what the GET
// sends (authentication/views/stores.py::_get_settings) and what a PATCH of
// that slice carries back. Store Settings reads the block STRICTLY (no
// substituted defaults) and sends it whole (pages/storeSettingsForms.js), so
// this list is the contract: a field the endpoint starts sending is invisible
// until it appears here, and one it stops sending fails the read loudly.
//
// `preferred_order_day` is the only nullable member — null exactly when the
// cadence is DAILY. The reader tells that null apart from a missing key,
// because they are different failures.
export const PLANNING_SETTINGS_FIELDS = [
  'planning_horizon_days',
  'emergency_alerts_before_order_day',
  'inventory_target_mode',
  'target_days_of_supply',
  'lead_time_days',
  'review_frequency',
  'preferred_order_day',
];

// Review cadences — the order in which every picker renders them (store
// default at install and in settings, per-supplier override), mirroring
// `REVIEW_FREQUENCY_CHOICES` in backend/core/models.py. A value not in this
// list is rejected by the serializer.
export const REVIEW_FREQUENCIES = ['DAILY', 'WEEKLY', 'MONTHLY'];

// Cadences anchored on a weekday, and so requiring `preferred_order_day`
// (mirrors `ANCHORED_REVIEW_FREQUENCIES` in backend/core/models.py). One weekday, two
// readings: WEEKLY orders on every occurrence of it, MONTHLY on the first
// occurrence of it each month. DAILY forbids the field entirely — the
// backend nulls it, so every picker clears it on the way in.
export const ANCHORED_REVIEW_FREQUENCIES = ['WEEKLY', 'MONTHLY'];

export const requiresPreferredOrderDay = (reviewFrequency) =>
  ANCHORED_REVIEW_FREQUENCIES.includes(reviewFrequency);

// The `PreferredOrderDay` numbering, 0=Monday … 6=Sunday (mirrors
// `PREFERRED_DAY_CHOICES` in backend/core/models.py). Every day picker and
// every strict reader of a stored day iterates or checks membership of THIS
// list — one home, so a picker cannot offer a day the model refuses.
export const ORDER_DAYS = [0, 1, 2, 3, 4, 5, 6];

// The weekday an anchored cadence lands on when nobody has chosen one yet:
// Monday, index 0 in the `PreferredOrderDay` numbering (0=Monday … 6=Sunday).
// The pickers preselect it through `orderDayForCadence` the moment a cadence
// becomes anchored (Store Settings, the Supplier setup grid, the
// create-supplier dialog).
//
// backend/core/models.py `DEFAULT_PREFERRED_ORDER_DAY` holds the same value
// (the default of `Store.preferred_order_day`), and
// purchase_orders/test_enum_mirror_contracts.py pins this file to it.
export const DEFAULT_PREFERRED_ORDER_DAY = 0;

/**
 * The weekday a cadence change leaves behind — the paired half of
 * `review_frequency`, resolved so a caller never has to restate the rule.
 *
 * The two fields are ONE decision to the endpoint: `order_cadence_error`
 * (backend/core/models.py) rejects an anchored cadence with no day AND a day
 * sent alongside DAILY, so a screen that changes the frequency without
 * changing the day in the same request gets a 400 naming a control the
 * merchant did not touch.
 *
 * Anchoring keeps a day already chosen and falls to
 * DEFAULT_PREFERRED_ORDER_DAY when there is none; unanchoring drops it.
 *
 * @param {string} reviewFrequency A REVIEW_FREQUENCIES value.
 * @param {number|null} storedDay The STORED representation — 0..6, or null
 *   for "no day", which is what every caller holds.
 * @returns {number|null} The day to send with `reviewFrequency`.
 */
export const orderDayForCadence = (reviewFrequency, storedDay) => {
  if (!requiresPreferredOrderDay(reviewFrequency)) return null;
  return storedDay === null ? DEFAULT_PREFERRED_ORDER_DAY : storedDay;
};

// Both anchored cadences store the SAME weekday, and read it differently:
// WEEKLY orders on every occurrence, MONTHLY on the first occurrence of that
// weekday each month (`_next_monthly_anchor` in
// purchase_orders_engine/order_windows.py). That difference is invisible in
// the stored value, so every surface that RENDERS a preferred order day has to
// resolve it — "Monday" under a monthly supplier means "first Monday", and a
// picker that just says "Monday" is telling the merchant something untrue.
//
// Callers ask it what a stored day MEANS through
// `pages/supplierSchedule.orderDayLabel` (Store Settings, the supplier grid,
// its editor and paste parser, the create-supplier dialog) and
// utils/dayOfWeekPickerColDef.js. Pinned in planning.test.js.
export const usesFirstWeekdayOfMonth = (reviewFrequency) =>
  reviewFrequency === 'MONTHLY';
